import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { CMAComp } from "@/hooks/useCMAData";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";

interface CMAMapProps {
  subjectAddress: string;
  subjectLat?: number | null;
  subjectLng?: number | null;
  comps: CMAComp[];
  primaryColor: string;
}

interface Coords {
  lng: number;
  lat: number;
}

async function geocodeAddress(
  address: string,
  token: string,
  proximity?: [number, number]
): Promise<Coords | null> {
  try {
    const prox = proximity ? `&proximity=${proximity[0]},${proximity[1]}` : "";
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${token}&limit=1${prox}`
    );
    const data = await res.json();
    const coords = data.features?.[0]?.geometry?.coordinates;
    return coords ? { lng: coords[0], lat: coords[1] } : null;
  } catch {
    return null;
  }
}

export function CMAMap({
  subjectAddress,
  subjectLat,
  subjectLng,
  comps,
  primaryColor,
}: CMAMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [webglError, setWebglError] = useState(false);
  const [subjectCoords, setSubjectCoords] = useState<[number, number] | null>(
    subjectLat != null && subjectLng != null ? [subjectLng, subjectLat] : null
  );
  const [geocodedComps, setGeocodedComps] = useState<Array<CMAComp & Coords>>([]);

  // 1. Fetch Mapbox token (demo mode — works on public pages)
  useEffect(() => {
    supabase.functions
      .invoke("get-mapbox-token", { body: { demo: true } })
      .then(({ data }) => { if (data?.token) setMapboxToken(data.token); })
      .catch(() => {});
  }, []);

  // 2. Geocode subject if no coords from property_data
  useEffect(() => {
    if (!mapboxToken || subjectCoords) return;
    geocodeAddress(subjectAddress, mapboxToken).then((pt) => {
      if (pt) setSubjectCoords([pt.lng, pt.lat]);
    });
  }, [mapboxToken, subjectAddress, subjectCoords]);

  // 3. Geocode comp addresses in parallel
  useEffect(() => {
    if (!mapboxToken || !subjectCoords || comps.length === 0) return;
    Promise.all(
      comps.map(async (comp) => {
        const addr = [comp.address, comp.city, comp.state, comp.zip].filter(Boolean).join(", ");
        if (!addr) return null;
        const pt = await geocodeAddress(addr, mapboxToken, subjectCoords);
        return pt ? { ...comp, ...pt } : null;
      })
    ).then((results) => {
      setGeocodedComps(results.filter(Boolean) as Array<CMAComp & Coords>);
    });
  }, [mapboxToken, comps, subjectCoords]);

  // 4. Initialize map when token + subject coords ready
  useEffect(() => {
    if (!mapboxToken || !mapContainer.current || !subjectCoords || map.current) return;
    try {
      mapboxgl.accessToken = mapboxToken;
      const m = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/mapbox/light-v11",
        center: subjectCoords,
        zoom: 13,
        interactive: true,
        failIfMajorPerformanceCaveat: false,
      });
      map.current = m;
      m.on("load", () => setMapLoaded(true));
      m.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");
    } catch {
      setWebglError(true);
    }
    return () => {
      map.current?.remove();
      map.current = null;
      setMapLoaded(false);
    };
  }, [mapboxToken, subjectCoords]);

  // 5. Place markers when map loaded + comps geocoded
  useEffect(() => {
    if (!mapLoaded || !map.current || !subjectCoords) return;

    // Clear previous markers
    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const bounds = new mapboxgl.LngLatBounds();

    // Subject property — larger, brand-colored, glowing ring
    const subjectEl = document.createElement("div");
    subjectEl.style.cssText = `
      width: 20px; height: 20px;
      background: ${primaryColor};
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 0 0 3px ${primaryColor}55, 0 2px 8px rgba(0,0,0,0.35);
      cursor: pointer;
    `;
    const subjectPopup = new mapboxgl.Popup({ offset: 22, maxWidth: "200px" }).setHTML(`
      <div style="font-family:system-ui,sans-serif;padding:2px;">
        <p style="font-weight:700;font-size:13px;margin:0 0 3px;color:#111;">Subject Property</p>
        <p style="color:#666;font-size:12px;margin:0;">${subjectAddress}</p>
      </div>
    `);
    markersRef.current.push(
      new mapboxgl.Marker(subjectEl)
        .setLngLat(subjectCoords)
        .setPopup(subjectPopup)
        .addTo(map.current)
    );
    bounds.extend(subjectCoords);

    // Comp markers
    for (const comp of geocodedComps) {
      const isSold = comp.status === "SOLD" || comp.status?.includes("SOLD");
      const isPending = comp.status === "PENDING";
      const color = isSold ? "#6b7280" : isPending ? "#f59e0b" : "#16a34a";
      const statusLabel = isSold ? "Sold" : isPending ? "Pending" : "Active";

      const el = document.createElement("div");
      el.style.cssText = `
        width: 14px; height: 14px;
        background: ${color};
        border-radius: 50%;
        border: 2px solid white;
        box-shadow: 0 1px 4px rgba(0,0,0,0.25);
        cursor: pointer;
      `;

      const details = [
        comp.bedrooms ? `${comp.bedrooms} bd` : null,
        comp.bathrooms ? `${comp.bathrooms} ba` : null,
        comp.square_feet ? `${comp.square_feet.toLocaleString()} sqft` : null,
      ].filter(Boolean).join(" · ");

      const popup = new mapboxgl.Popup({ offset: 15, maxWidth: "220px" }).setHTML(`
        <div style="font-family:system-ui,sans-serif;padding:2px;">
          ${comp.image_url ? `<img src="${comp.image_url}" style="width:100%;height:80px;object-fit:cover;border-radius:4px;margin-bottom:6px;" />` : ""}
          <p style="font-weight:600;font-size:13px;margin:0 0 1px;color:#111;">${comp.address || ""}</p>
          <p style="color:#888;font-size:11px;margin:0 0 4px;">${statusLabel}</p>
          ${comp.price ? `<p style="font-weight:700;font-size:14px;color:#111;margin:0 0 3px;">$${comp.price.toLocaleString()}</p>` : ""}
          ${details ? `<p style="color:#888;font-size:11px;margin:0;">${details}</p>` : ""}
        </div>
      `);

      markersRef.current.push(
        new mapboxgl.Marker(el)
          .setLngLat([comp.lng, comp.lat])
          .setPopup(popup)
          .addTo(map.current!)
      );
      bounds.extend([comp.lng, comp.lat]);
    }

    if (geocodedComps.length > 0 && !bounds.isEmpty()) {
      map.current.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 700 });
    }
  }, [mapLoaded, subjectCoords, geocodedComps, primaryColor, subjectAddress]);

  if (webglError) return null;
  if (!mapboxToken) return null;

  return (
    <div>
      <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm relative">
        {!mapLoaded && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-50 z-10">
            <div className="h-5 w-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
          </div>
        )}
        <div ref={mapContainer} className="w-full h-[420px] md:h-[500px]" />
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-5 mt-3 text-xs text-slate-400">
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block w-3 h-3 rounded-full border-2 border-white shadow"
            style={{ background: primaryColor }}
          />
          Subject Property
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-white shadow bg-green-600" />
          Active
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-white shadow bg-amber-500" />
          Pending
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-full border-2 border-white shadow bg-slate-400" />
          Sold
        </span>
      </div>
    </div>
  );
}
